import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Container,
  Grid,
  Paper
} from '@mui/material';
import { CheckCircleOutline, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate,useLocation } from 'react-router-dom';

// Import your service functions
import { getActionDetails } from '../../service/Actions/getAction';
import { getActionComments } from '../../service/Actions/getActionComments';
import { addActionComment } from '../../service/Actions/addActionComment';
import { completeAction } from '../../service/Actions/completeAction';
import { deleteAction } from '../../service/Actions/deleteAction';
import { updateAction } from '../../service/Actions/updateAction';

const ActionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { actionID, fromScreen } = location.state || {};




  const [actionDetails, setActionDetails] = useState(null);
  const [isLoadingActionDetails, setIsLoadingActionDetails] = useState(true);

  const [comments, setComments] = useState(null);
  const [isLoadingComments, setIsLoadingComments] = useState(true);

  const [isModalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);

  const [modalStatusMessage, setModalStatusMessage] = useState(null);
  const [isModalSendingRequestNow, setIsModalSendingRequestNow] = useState(false);

  const [actionDetailText, onChangeActionDetailText] = useState('');
  const [addCommentInputText, setAddCommentInputText] = useState('');

  const [datepickerDate, setDatepickerDate] = useState(new Date());
  const [newDateDue, setNewDateDue] = useState(null);

  const fetchAction = async () => {
    try {
      setIsLoadingActionDetails(true);
      const resData = await getActionDetails(actionID);
      setIsLoadingActionDetails(false);
      if (resData.status === 200) {
        const fetchedData = resData.data;
        if (Array.isArray(fetchedData) && fetchedData.length === 1) {
          setActionDetails({ ...fetchedData[0] });
        } else {
          setActionDetails({ ...fetchedData });
        }
      } else {
        setActionDetails({ error: 'Error fetching action details, please try again later.' });
      }
    } catch (error) {
      setIsLoadingActionDetails(false);
      setActionDetails({ error: 'Error fetching action details, please try again later.' });
    }
  };

  const fetchComments = async () => {
    try {
      const resData = await getActionComments(actionID);
      setIsLoadingComments(false);
      if (resData.status === 200) {
        setComments([...resData.data]);
      } else {
        setComments({ error: 'Error fetching comments' });
      }
    } catch (error) {
      setComments({ error: 'Error fetching comments' });
      setIsLoadingComments(false);
    }
  };

  const renderComments = (comments) => {
    if (isLoadingComments) {
      return <Typography>Loading comments...</Typography>;
    } else if (Array.isArray(comments) && comments.length > 0) {
      return comments.map(item => (
        <Card key={item.actionCommentID} sx={{ mb: 1 }}>
          <CardContent>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {item.comment}
            </Typography>
            <Divider />
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {item.commentFirstName} {item.commentLastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {item.dateCommented}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ));
    } else if (Array.isArray(comments) && comments.length === 0) {
      return (
        <Card>
          <CardContent>
            <Typography>There is no comment for this action.</Typography>
          </CardContent>
        </Card>
      );
    } else if (comments?.error) {
      return (
        <Card>
          <CardContent>
            <Typography>Error fetching comments, please try again later.</Typography>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Card>
          <CardContent>
            <Typography>Error fetching comments, please try again later.</Typography>
          </CardContent>
        </Card>
      );
    }
  };

  const handleUpdateAction = async () => {
    setIsModalSendingRequestNow(true);
    setModalStatusMessage('Updating action now, please wait...');
    try {
      let updateBody = {};
      if (newDateDue) {
        updateBody["DateDue"] = newDateDue;
      }
      if (actionDetailText && actionDetailText !== actionDetails.details) {
        updateBody["details"] = actionDetailText;
      }

      if (updateBody.DateDue || updateBody.details) {
        const updateActionRes = await updateAction(actionDetails.actionID, updateBody);
        setIsModalSendingRequestNow(false);
        if (updateActionRes.status === 200) {
          setNewDateDue(null);
          setModalVisible(false);
          setModalStatusMessage(null);

          let modifiedActionData = {
            actionID: actionDetails.actionID,
            isUpdatedAction: true,
            updatedActionID: actionDetails.actionID,
            fromScreen: 'ActionDetailsScreen',
            updateBody: { ...updateBody }
          };

          if (updateBody.DateDue) {
            modifiedActionData.oldActionDetails = actionDetails;
          }

          if (fromScreen === 'OverdueActionsScreen') {
            navigate('/actions/overdue', { state: modifiedActionData });
          } else if (fromScreen === 'UpcomingActionsScreen') {
            navigate('/actions/upcoming', { state: modifiedActionData });
          } else {
            fetchAction();
          }
        } else {
          setModalStatusMessage('Error updating action, please try again later.');
        }
      } else {
        setIsModalSendingRequestNow(false);
        setModalStatusMessage('No change was made to this action, no need to update.');
      }
    } catch (error) {
      setIsModalSendingRequestNow(false);
      setModalStatusMessage('Error updating action, please try again later.');
    }
  };

  const handleAddComment = async () => {
    if (!addCommentInputText || addCommentInputText.trim().length === 0) {
      setModalStatusMessage('Please type a valid comment');
      return;
    }
    setIsModalSendingRequestNow(true);
    setModalStatusMessage('Saving comment now, please wait...');

    const addCommentRes = await addActionComment(actionDetails.actionID, addCommentInputText);
    setIsModalSendingRequestNow(false);

    if (addCommentRes.status === 200) {
      setModalVisible(false);
      setAddCommentInputText('');
      setModalStatusMessage(null);
      fetchComments();
    } else {
      setModalStatusMessage('Error adding comment, please try again.');
    }
  };

  const handleCompleteAction = async () => {
    setIsModalSendingRequestNow(true);
    setModalStatusMessage('Completing action now, please wait...');

    let modifiedActionData = {
      actionID: actionDetails.actionID,
      isCompletedAction: true,
      completedActionID: actionDetails.actionID,
      fromScreen: 'ActionDetailsScreen'
    };

    const completeActionRes = await completeAction(actionDetails.actionID);
    setIsModalSendingRequestNow(false);

    if (completeActionRes.status === 200 && completeActionRes.data.actionID === actionDetails.actionID) {
      setModalVisible(false);
      setModalStatusMessage(null);

      if (fromScreen && fromScreen.length > 0) {
        if (fromScreen === 'OverdueActionsScreen') {
          navigate('/actions/overdue', { state: modifiedActionData });
        } else if (fromScreen === 'UpcomingActionsScreen') {
          navigate('/actions/upcoming', { state: modifiedActionData });
        }
      } else {
        fetchAction();
      }
    } else {
      setModalStatusMessage('Error completing action, please try again.');
    }
  };

  const handleDeleteAction = async () => {
    setIsModalSendingRequestNow(true);
    setModalStatusMessage('Deleting action now, please wait...');
    const deleteActionRes = await deleteAction(actionDetails.actionID);
    setIsModalSendingRequestNow(false);

    if (deleteActionRes.status === 200 && deleteActionRes.data.actionID === actionDetails.actionID) {
      let modifiedActionData = {
        actionID: actionDetails.actionID,
        isDeletedAction: true,
        deletedActionID: actionDetails.actionID,
        fromScreen: 'ActionDetailsScreen'
      };

      setModalVisible(false);
      setModalStatusMessage(null);

      if (fromScreen && fromScreen.length > 0) {
        if (fromScreen === 'OverdueActionsScreen') {
          navigate('/actions/overdue', { state: modifiedActionData });
        } else if (fromScreen === 'UpcomingActionsScreen') {
          navigate('/actions/upcoming', { state: modifiedActionData });
        }
      } else {
        navigate(-1); // Go back
      }
    } else {
      setModalStatusMessage('Error deleting action, please try again.');
    }
  };

  useEffect(() => {
    fetchAction();
    fetchComments();
  }, []);

  const setBackGroundColorForActionType = (category) => {
    if (category === 'Listing') {
      return '#4CAF50';
    } else if (category === 'Contact') {
      return '#9c9b96';
    } else {
      return "orange";
    }
  };

  if (isLoadingActionDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (actionDetails && actionDetails.actionID == null) {
    return (
      <Box p={2}>
        <Typography>No details for this action or the action might be deleted.</Typography>
      </Box>
    );
  }

  if (!isLoadingActionDetails && actionDetails && actionDetails.error) {
    return (
      <Box p={2}>
        <Typography color="error">{actionDetails.error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ pb: 10 }}>
        {/* Action Details Card */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              {actionDetails.DateDue && (
                <Typography variant="body1">
                  Due: <strong>{actionDetails.DateDue}</strong>
                  {actionDetails.Complete === 1 && (
                    <CheckCircleOutline sx={{ ml: 1, color: '#228B22' }} />
                  )}
                </Typography>
              )}
              {actionDetails.DateSet && (
                <Typography variant="caption" color="text.secondary">
                  Set: {actionDetails.DateSet}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {actionDetails.Category && `Category: ${actionDetails.Category.toUpperCase()}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {actionDetails.actionID && `Action ID: ${actionDetails.actionID}`}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Action Details */}
        {actionDetails.details && (
          <>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Action Details
            </Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography>{actionDetails.details}</Typography>
              </CardContent>
            </Card>
          </>
        )}

        {/* Property Address */}
        {actionDetails.propertyAddress && (
          <>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Property Address
            </Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography>{actionDetails.propertyAddress}</Typography>
                {actionDetails.PropertyID && (
                  <Typography variant="caption" color="text.secondary" align="right" display="block">
                    Property ID: {actionDetails.PropertyID}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Contact Information */}
        {actionDetails.contactName && (
          <>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Contact
            </Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                {actionDetails.contactName && <Typography>Name: {actionDetails.contactName}</Typography>}
                {actionDetails.contactPhoneNumber && <Typography>Phone: {actionDetails.contactPhoneNumber}</Typography>}
                {actionDetails.contactEmail && <Typography>Email: {actionDetails.contactEmail}</Typography>}
              </CardContent>
            </Card>
          </>
        )}

        {/* Comments Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Comments
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setModalVisible(true);
              setModalType('addComment');
            }}
            sx={{ bgcolor: '#6c757d' }}
          >
            ADD COMMENT
          </Button>
        </Box>

        {/* Comments List */}
        <Box mb={2}>
          {renderComments(comments)}
        </Box>

        {/* Modal/Dialog */}
        <Dialog
          open={isModalVisible}
          onClose={() => setModalVisible(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            {modalType === 'editAction' && actionDetails && (
              <Box>
                <Typography variant="h6" mb={2}>Edit Action</Typography>
                
                <Typography variant="subtitle1" mb={1}>
                  Due Date: {actionDetails.DateDue}
                </Typography>
                {newDateDue && (
                  <Typography variant="subtitle1" mb={2}>
                    New Due Date: {newDateDue}
                  </Typography>
                )}

                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                  <DatePicker
                    label="Change Due Date"
                    value={datepickerDate}
                    onChange={(date) => {
                      setDatepickerDate(date);
                      const formattedDateString = date.toISOString().split('T')[0];
                      setNewDateDue(formattedDateString);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  />
                </LocalizationProvider>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" mb={1}>Details:</Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={actionDetailText}
                  onChange={(e) => onChangeActionDetailText(e.target.value)}
                  margin="normal"
                />

                {modalStatusMessage && (
                  <Typography variant="body2" color="info" align="right" mt={1}>
                    {modalStatusMessage}
                  </Typography>
                )}
              </Box>
            )}

            {(modalType === 'addComment' || modalType === 'completeAction') && actionDetails && (
              <Box>
                <Typography variant="h6" mb={2}>Add Comment</Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder={modalType === 'completeAction' ? 'Add comment and complete action' : 'Type comment here...'}
                  value={addCommentInputText}
                  onChange={(e) => setAddCommentInputText(e.target.value)}
                  margin="normal"
                />

                {modalStatusMessage && (
                  <Typography variant="body2" color="info" align="right" mt={1}>
                    {modalStatusMessage}
                  </Typography>
                )}
              </Box>
            )}

            {modalType === 'deleteAction' && actionDetails && (
              <Box>
                <Typography variant="h6" mb={2}>Delete this action?</Typography>
                <Divider sx={{ my: 2 }} />
                
                {modalStatusMessage && (
                  <Typography variant="body2" color="info" align="right" mt={1}>
                    {modalStatusMessage}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            {modalType === 'editAction' && (
              <>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    setNewDateDue(null);
                    setModalStatusMessage(null);
                  }}
                  disabled={isModalSendingRequestNow}
                  color="secondary"
                >
                  Close
                </Button>
                <Button
                  onClick={handleUpdateAction}
                  disabled={isModalSendingRequestNow}
                  color="primary"
                  variant="contained"
                >
                  Update
                </Button>
              </>
            )}

            {(modalType === 'addComment' || modalType === 'completeAction') && (
              <>
                <Button
                  onClick={() => setModalVisible(false)}
                  disabled={isModalSendingRequestNow}
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (addCommentInputText && addCommentInputText.length > 0) {
                      handleAddComment();
                    }
                    if (modalType === 'completeAction') {
                      handleCompleteAction();
                    }
                  }}
                  disabled={isModalSendingRequestNow}
                  color="primary"
                  variant="contained"
                >
                  {modalType === 'completeAction' ? 'COMPLETE ACTION' : 'SAVE'}
                </Button>
              </>
            )}

            {modalType === 'deleteAction' && (
              <>
                <Button
                  onClick={() => setModalVisible(false)}
                  disabled={isModalSendingRequestNow}
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteAction}
                  disabled={isModalSendingRequestNow}
                  color="error"
                  variant="contained"
                >
                  Delete
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>

      {/* Fixed Bottom Buttons */}
      {actionDetails.Complete !== 1 && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            bgcolor: 'white',
            zIndex: 1000
          }}
        >
          <Container maxWidth="md">
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button
                onClick={() => {
                  setModalVisible(true);
                  setModalType('deleteAction');
                }}
                color="error"
                variant="contained"
              >
                DELETE
              </Button>
              <Box display="flex" gap={2}>
                <Button
                  onClick={() => {
                    setModalVisible(true);
                    setModalType('editAction');
                    onChangeActionDetailText(actionDetails.details || '');
                  }}
                  variant="contained"
                  sx={{ bgcolor: '#BEB18B' }}
                >
                  EDIT
                </Button>
                <Button
                  onClick={() => {
                    if (actionDetails.Complete === 0) {
                      setModalVisible(true);
                      setModalType('completeAction');
                    }
                  }}
                  color="success"
                  variant="contained"
                >
                  {actionDetails.Complete === 1 ? 'ACTION COMPLETED' : 'COMPLETE'}
                </Button>
              </Box>
            </Box>
          </Container>
        </Paper>
      )}
    </Container>
  );
};

export default ActionDetails;